import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../../shared/helper/DomHelper";
import MouseMode from "../../../viewer/settings/MouseMode";
import MouseFunction from "../../../viewer/settings/MouseFunction";
import {
	BorderStyle,
	DrawMode,
	Point,
	Rectangle,
	TextAnnotationIcons
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import NumberHelper from "../../../../shared/helper/NumberHelper";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import {ApprovedStamp} from "../../../../shared/annotation/stamp/ApprovedStamp";
import {ExperimentalStamp} from "../../../../shared/annotation/stamp/ExperimentalStamp";
import {NotApprovedStamp} from "../../../../shared/annotation/stamp/NotApprovedStamp";
import {AsIsStamp} from "../../../../shared/annotation/stamp/AsIsStamp";
import {ExpiredStamp} from "../../../../shared/annotation/stamp/ExpiredStamp";
import {NotForPublicReleaseStamp} from "../../../../shared/annotation/stamp/NotForPublicReleaseStamp";
import {ConfidentialStamp} from "../../../../shared/annotation/stamp/ConfidentialStamp";
import {FinalStamp} from "../../../../shared/annotation/stamp/FinalStamp";
import {SoldStamp} from "../../../../shared/annotation/stamp/SoldStamp";
import {DepartmentalStamp} from "../../../../shared/annotation/stamp/DepartmentalStamp";
import {ForCommentStamp} from "../../../../shared/annotation/stamp/ForCommentStamp";
import {TopSecretStamp} from "../../../../shared/annotation/stamp/TopSecretStamp";
import {DraftStamp} from "../../../../shared/annotation/stamp/DraftStamp";
import {ForPublicReleaseStamp} from "../../../../shared/annotation/stamp/ForPublicReleaseStamp";
import {SVG_NAMESPACE} from "../../../../shared/annotation/Annotation";
import MenuButton from "../../../../shared/menu/MenuButton";
import Translate from "../../../../shared/translation/Translate";
import {HelpIcon} from "../../../../shared/annotation/icon/HelpIcon";
import {InsertIcon} from "../../../../shared/annotation/icon/InsertIcon";
import {KeyIcon} from "../../../../shared/annotation/icon/KeyIcon";
import {NewParagraphIcon} from "../../../../shared/annotation/icon/NewParagraphIcon";
import {ParagraphIcon} from "../../../../shared/annotation/icon/ParagraphIcon";
import {NoteIcon} from "../../../../shared/annotation/icon/NoteIcon";
import {CheckIcon} from "../../../../shared/annotation/icon/CheckIcon";
import {CircleIcon} from "../../../../shared/annotation/icon/CircleIcon";
import {CrossIcon} from "../../../../shared/annotation/icon/CrossIcon";
import {CrossHairIcon} from "../../../../shared/annotation/icon/CrossHairIcon";
import {RightArrowIcon} from "../../../../shared/annotation/icon/RightArrowIcon";
import {RightPointerIcon} from "../../../../shared/annotation/icon/RightPointerIcon";
import {StarIcon} from "../../../../shared/annotation/icon/StarIcon";
import {UpArrowIcon} from "../../../../shared/annotation/icon/UpArrowIcon";
import {UpLeftArrowIcon} from "../../../../shared/annotation/icon/UpLeftArrowIcon";
import {CommentIcon} from "../../../../shared/annotation/icon/CommentIcon";
import {CustomStamp} from "../../../../shared/annotation/stamp/CustomStamp";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import AnnotationEditBoxDialog from "../../../dialog/components/annotation/AnnotationEditBoxDialog";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import UserSettings from "../../../session/UserSettings";
import {noteIconSize} from "../pdf/annotation/AnnotationNoteFunction";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {ContextMenu} from "primereact/contextmenu";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";
import {MenuItem} from "primereact/menuitem";

enum ResizeDirection {
	TOP_LEFT,
	TOP_CENTER,
	TOP_RIGHT,
	CENTER_LEFT,
	CENTER_RIGHT,
	BOTTOM_LEFT,
	BOTTOM_CENTER,
	BOTTOM_RIGHT
}

interface RectSelectionProperties extends I18nGlobals, DebugGlobals {
	mouseMode?: MouseMode;
	mouseFunction?: MouseFunction;
	width?: number; // Apply a fixed width, which can't be changed
	height?: number; // Apply a fixed height, which can't be changed
	zoomValue?: number;
	startCallback?: (options: Rectangle | null, event?: React.MouseEvent) => void;
	moveCallback?: (options: Rectangle | null, event?: MouseEvent) => void;
	endCallback?: (options: Rectangle | null, apply: boolean) => void;
	resizeable?: boolean;
	interiorColor?: string;
	borderColor?: string;
	borderStyle?: BorderStyle;
	borderWidth?: number;
	drawMode?: DrawMode;
	opacity?: number;
	handleInteractivity?: () => void;
	confirmation?: boolean;
	icon?: TextAnnotationIcons;
	content?: string;
	fontColor?: string;
	fontSize?: number;
	context: PortalContext;
	userSettings?: Immutable<UserSettings>;
	getParent: () => HTMLElement | null;
}

interface RectSelectionState {
	lastMousePosition: Point;
	startPosition: Point;
	rect: Rectangle;
	active: boolean;
	edit: boolean;
	move: boolean;
	direction: ResizeDirection;
	showBoxEditDialog: boolean;
}

export default class RectSelection extends PortalPureComponent<RectSelectionProperties, RectSelectionState> {
	private checking: boolean;
	private contextMenu: RefObject<ContextMenu>;

	constructor(props: Readonly<RectSelectionProperties>) {
		super(props);

		let zoomScale: number = FunctionHelper.applyValue(props.zoomValue, 1) / 100;

		this.checking = false;
		this.state = {
			lastMousePosition: Point.fromJson({
				x: 0,
				y: 0
			}),
			startPosition: Point.fromJson({
				x: 0,
				y: 0
			}),
			rect: Rectangle.fromJson({
				width: FunctionHelper.applyValue(props.width, 0) * zoomScale,
				height: FunctionHelper.applyValue(props.height, 0) * zoomScale,
				x: 0,
				y: 0
			} as Rectangle),
			active: false,
			edit: false,
			move: false,
			direction: ResizeDirection.BOTTOM_RIGHT,
			showBoxEditDialog: false
		};

		this.contextMenu = createRef<ContextMenu>();

		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handleMove = this.handleMove.bind(this);
		this.handleDoubleClick = this.handleDoubleClick.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleClick = this.handleClick.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.addEventListener("pointerdown", this.handleMouseDown);
		}

		// apply global events so events get applied outside the browser
		window.addEventListener("pointerup", this.handleMouseUp);
		window.addEventListener("pointermove", this.handleMouseMove);
		window.addEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.removeEventListener("pointerdown", this.handleMouseDown);
		}

		window.removeEventListener("pointerup", this.handleMouseUp);
		window.removeEventListener("pointermove", this.handleMouseMove);
		window.removeEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<RectSelectionProperties>, prevState: Readonly<RectSelectionState>, snapshot?: any
	): Promise<void> {
		let updates: any = {};
		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let previousZoomScale: number = FunctionHelper.applyValue(prevProps.zoomValue, 1) / 100;

		if (this.props.mouseMode !== prevProps.mouseMode || this.props.mouseFunction !== prevProps.mouseFunction) {
			updates.active = FunctionHelper.exists(this.props.width) && FunctionHelper.exists(this.props.height);

			// If fixed dimensions show rect directly
			if (updates.active) {
				updates = {
					...updates,
					rect: Rectangle.fromJson({
						x: 0,
						y: 0,
						width: this.props.width! * zoomScale,
						height: this.props.height! * zoomScale
					})
				} as RectSelectionState
			}
		}

		if (zoomScale !== previousZoomScale) {
			updates = {
				...updates,
				rect: Rectangle.fromJson({
					x: (this.state.rect.x! / previousZoomScale) * zoomScale,
					y: (this.state.rect.y! / previousZoomScale) * zoomScale,
					width: (this.state.rect.width! / previousZoomScale) * zoomScale,
					height: (this.state.rect.height! / previousZoomScale) * zoomScale
				})
			} as RectSelectionState
		}

		if (Object.keys(updates).length > 0) {
			this.setState(updates);
		}
	}

	private async handleKeyDown(event: KeyboardEvent): Promise<void> {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || !this.state.active || this.state.showBoxEditDialog) {
			return;
		}

		let rect: Rectangle = this.state.rect;
		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let mod: number = event.ctrlKey ? 5 : 1;

		switch (event.key.toLowerCase()) {
			case "arrowup":
				event.preventDefault();
				if (event.shiftKey) {
					if (rect.height! - mod <= 0) {
						rect.height! = 0;
						return;
					}

					rect.height! -= mod;
					break;
				}

				if (rect.y! - mod <= 0) {
					rect.y! = 0;
					return;
				}

				rect.y! -= mod;
				break;
			case "arrowdown":
				event.preventDefault();
				if (event.shiftKey) {
					if (rect.height! + mod + rect.y! >= containerRect.height) {
						rect.height! = containerRect.height - rect.y!;
						return;
					}

					rect.height! += mod;
					break;
				}

				if (rect.y! + mod + rect.height! >= containerRect.height) {
					rect.y! = containerRect.height - rect.height!;
					return;
				}

				rect.y! += mod;
				break;
			case "arrowleft":
				event.preventDefault();
				if (event.shiftKey) {
					if (rect.width! - mod <= 0) {
						rect.width! = 0;
						return;
					}

					rect.width! -= mod;
					break;
				}

				if (rect.x! - mod <= 0) {
					rect.x! = 0;
					return;
				}

				rect.x! -= mod;
				break;
			case "arrowright":
				event.preventDefault();
				if (event.shiftKey) {
					if (rect.width! + mod + rect.x! >= containerRect.width) {
						rect.width! = containerRect.width - rect.x!;
						return;
					}

					rect.width! += mod;
					break;
				}

				if (rect.x! + mod + this.state.rect.width! >= containerRect.width) {
					rect.x! = containerRect.width - rect.width!;
					return;
				}

				rect.x! += mod;
				break;
		}

		this.setState({
			rect: rect.clone()
		}, async () => {
			if (FunctionHelper.exists(this.props.endCallback)) {
				await this.props.endCallback!(this.state.rect, true);
			}
		});
	}

	private async handleDoubleClick(): Promise<void> {
		if (!this.props.confirmation) {
			return;
		}

		if (FunctionHelper.exists(this.props.endCallback)) {
			this.props.endCallback!(this.state.rect, true);
		}
	}

	private async handleMouseUp(event: MouseEvent): Promise<void> {
		if (!this.state.active || !this.state.edit || event.button !== 0) {
			return;
		}

		event.stopPropagation();

		if (FunctionHelper.exists(this.props.endCallback)) {
			this.props.endCallback!(this.state.rect, !this.props.confirmation);
		}

		let isClick = false;
		let rect: Rectangle = this.state.rect;

		if (rect.width === 0 && rect.height === 0) {
			isClick = true;
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let updates: any = {};
		if (this.props.mouseMode !== MouseMode.RECTANGLE) {
			updates = {
				active: false,
				rect: Rectangle.fromJson({
					x: 0,
					y: 0,
					width: FunctionHelper.applyValue(this.props.width, 0) * zoomScale,
					height: FunctionHelper.applyValue(this.props.height, 0) * zoomScale
				})
			} as RectSelectionState
		}

		this.setState({
			...updates,
			edit: isClick,
			move: false,
			direction: ResizeDirection.BOTTOM_RIGHT
		});
	}

	private handleMouseDown(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || event.button !== 0 || !DomHelper.hasClass(event.target as HTMLElement, parent!.className)) {
			return;
		}

		event.stopPropagation();

		if (FunctionHelper.exists(this.props.handleInteractivity)) {
			this.props.handleInteractivity!();
		}

		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let xPos: number = event.clientX - containerRect.x;
		let yPos: number = event.clientY - containerRect.y;
		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;

		this.setState({
			lastMousePosition: Point.fromJson({
				x: xPos,
				y: yPos
			}),
			rect: Rectangle.fromJson({
				x: xPos,
				y: yPos,
				width: FunctionHelper.applyValue(this.props.width, 0) * zoomScale,
				height: FunctionHelper.applyValue(this.props.height, 0) * zoomScale
			}),
			active: true,
			edit: true
		}, () => {
			if (FunctionHelper.exists(this.props.startCallback)) {
				this.props.startCallback!(this.state.rect);
			}

			this.handleMouseMove(event);
		});
	}

	private handleMouseMove(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || !this.state.active || !this.state.edit || this.checking) {
			return;
		}

		event.stopPropagation();

		this.checking = true;
		let containerRect: DOMRect = parent!.getBoundingClientRect();

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let xPos: number = event.clientX - containerRect.x;
		let yPos: number = event.clientY - containerRect.y;
		let editX: boolean = true;
		let editY: boolean = true;

		if (this.props.resizeable) {
			switch (this.state.direction) {
				case ResizeDirection.CENTER_RIGHT:
				case ResizeDirection.CENTER_LEFT:
					yPos = this.state.rect.y!;
					editY = false;
					break;
				case ResizeDirection.TOP_CENTER:
				case ResizeDirection.BOTTOM_CENTER:
					xPos = this.state.rect.x!;
					editX = false;
					break;
			}
		}

		xPos = NumberHelper.limit(xPos, 0, containerRect.width);
		yPos = NumberHelper.limit(yPos, 0, containerRect.height);

		let width: number = this.state.rect.width!;
		let height: number = this.state.rect.height!;

		if (this.state.move) {
			if (editX) {
				xPos -= this.state.lastMousePosition.x!;

				if (xPos < 0) {
					xPos = 0;
				} else if (xPos + this.state.rect.width! > containerRect.width) {
					xPos = containerRect.width - this.state.rect.width!;
				}
			}

			if (editY) {
				yPos -= this.state.lastMousePosition.y!;

				if (yPos < 0) {
					yPos = 0;
				} else if (yPos + this.state.rect.height! > containerRect.height) {
					yPos = containerRect.height - this.state.rect.height!;
				}
			}
		} else {
			if (editX && !FunctionHelper.exists(this.props.width)) {
				width = xPos - this.state.lastMousePosition.x!;
				if (width >= 0) {
					xPos = this.state.lastMousePosition.x!;
				}

				width = Math.abs(width);
			}

			if (editY && !FunctionHelper.exists(this.props.height)) {
				height = yPos - this.state.lastMousePosition.y!;
				if (height >= 0) {
					yPos = this.state.lastMousePosition.y!;
				}

				height = Math.abs(height);
			}
		}

		if (FunctionHelper.exists(this.props.width)) {
			width = this.props.width! * zoomScale;
		}

		if (xPos + width > containerRect.width) {
			xPos = containerRect.width - width;
		}

		if (FunctionHelper.exists(this.props.height)) {
			height = this.props.height! * zoomScale;
		}

		if (yPos + height > containerRect.height) {
			yPos = containerRect.height - height;
		}

		this.setState({
			rect: Rectangle.fromJson({
				x: xPos,
				y: yPos,
				width: width,
				height: height
			})
		}, async () => {
			if (FunctionHelper.exists(this.props.moveCallback)) {
				await this.props.moveCallback!(this.state.rect, event);
			}

			this.checking = false;
		});
	}

	private handleResize(direction: ResizeDirection, event: React.MouseEvent): void {
		if (event.button !== 0) {
			return;
		}

		event.stopPropagation();

		let xStart: number = this.state.rect.x!;
		let yStart: number = this.state.rect.y!;

		switch (direction) {
			case ResizeDirection.BOTTOM_LEFT:
				xStart += this.state.rect.width!;
				break;
			case ResizeDirection.CENTER_LEFT:
				xStart += this.state.rect.width!;
				break;
			case ResizeDirection.TOP_RIGHT:
				yStart += this.state.rect.height!;
				break;
			case ResizeDirection.TOP_CENTER:
				yStart += this.state.rect.height!;
				break;
			case ResizeDirection.TOP_LEFT:
				xStart += this.state.rect.width!;
				yStart += this.state.rect.height!;
				break;
		}

		this.setState({
			edit: true,
			direction: direction,
			lastMousePosition: Point.fromJson({
				x: xStart,
				y: yStart
			})
		});
	}

	private handleMove(event: React.MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || event.button !== 0) {
			return;
		}

		event.stopPropagation();

		let containerRect: DOMRect = parent!.getBoundingClientRect();

		this.setState({
			move: true,
			edit: true,
			lastMousePosition: Point.fromJson({
				x: event.clientX - containerRect.x - this.state.rect.x!,
				y: event.clientY - containerRect.y - this.state.rect.y!
			})
		});
	}

	private renderRectPreview(): ReactNode {
		let mouseFunctions: Array<MouseFunction> = [
			MouseFunction.SQUARE,
			MouseFunction.REDACT_AREA
		];

		if (!mouseFunctions.includes(this.props.mouseFunction!)) {
			return null;
		}

		let drawMode: DrawMode = FunctionHelper.applyValue(this.props.drawMode, DrawMode.FillAndStroke);
		let interiorColor: string = FunctionHelper.applyValue(this.props.interiorColor, "#FFF");
		let borderColor: string = FunctionHelper.applyValue(this.props.borderColor, "#000");

		switch (drawMode) {
			case DrawMode.Fill:
				borderColor = "none";
				break;
			case DrawMode.Stroke:
				interiorColor = "none";
				break;
		}

		let borderStyle: BorderStyle = FunctionHelper.applyValue(this.props.borderStyle, BorderStyle.Solid);
		let borderWidth: number = FunctionHelper.applyValue(this.props.borderWidth, 1);
		let opacity: number = FunctionHelper.applyValue(this.props.opacity, 100) / 100;

		return (
			<svg xmlns={SVG_NAMESPACE} width={this.state.rect.width!} height={this.state.rect.height!}
				 style={{
					 opacity: opacity
				 }}>
				<rect x={0} y={0} width={this.state.rect.width!} height={this.state.rect.height!}
					  className="movearea" onPointerDown={this.handleMove}
					  onClick={this.handleClick} onContextMenu={this.handleClick}
					  style={{
						  strokeWidth: borderWidth,
					  }}/>
				<rect x={0} y={0} width={this.state.rect.width!} height={this.state.rect.height!}
					  style={{
						  strokeDasharray: borderStyle === BorderStyle.Solid ? "none" : 5,
						  strokeWidth: borderWidth,
						  stroke: borderColor,
						  fill: interiorColor
					  }}/>
			</svg>
		);
	}

	private renderCirclePreview(): ReactNode {
		let mouseFunctions: Array<MouseFunction> = [
			MouseFunction.CIRCLE
		];

		if (!mouseFunctions.includes(this.props.mouseFunction!)) {
			return null;
		}

		let drawMode: DrawMode = FunctionHelper.applyValue(this.props.drawMode, DrawMode.FillAndStroke);
		let interiorColor: string = FunctionHelper.applyValue(this.props.interiorColor, "#FFF");
		let borderColor: string = FunctionHelper.applyValue(this.props.borderColor, "#000");

		switch (drawMode) {
			case DrawMode.Fill:
				borderColor = "none";
				break;
			case DrawMode.Stroke:
				interiorColor = "none";
				break;
		}

		let borderStyle: BorderStyle = FunctionHelper.applyValue(this.props.borderStyle, BorderStyle.Solid);
		let borderWidth: number = FunctionHelper.applyValue(this.props.borderWidth, 1);
		let opacity: number = FunctionHelper.applyValue(this.props.opacity, 100) / 100;

		return (
			<svg xmlns={SVG_NAMESPACE} width={this.state.rect.width!} height={this.state.rect.height!}
				 style={{
					 opacity: opacity
				 }}>
				<ellipse cx={this.state.rect.width! / 2} cy={this.state.rect.height! / 2}
						 rx={this.state.rect.width! / 2} ry={this.state.rect.height! / 2}
						 className="movearea" onPointerDown={this.handleMove}
						 onClick={this.handleClick} onContextMenu={this.handleClick}
						 style={{
							 strokeWidth: borderWidth
						 }}/>
				<ellipse cx={this.state.rect.width! / 2} cy={this.state.rect.height! / 2}
						 rx={this.state.rect.width! / 2} ry={this.state.rect.height! / 2}
						 style={{
							 strokeDasharray: borderStyle === BorderStyle.Solid ? "none" : 3,
							 strokeWidth: borderWidth,
							 stroke: borderColor,
							 fill: interiorColor
						 }}/>
			</svg>
		);
	}

	private renderStampPreview(): ReactNode {
		let mouseFunctions: Array<MouseFunction> = [
			MouseFunction.STAMP_APPROVED,
			MouseFunction.STAMP_EXPERIMENTAL,
			MouseFunction.STAMP_NOT_APPROVED,
			MouseFunction.STAMP_AS_IS,
			MouseFunction.STAMP_EXPIRED,
			MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CONFIDENTIAL,
			MouseFunction.STAMP_FINAL,
			MouseFunction.STAMP_SOLD,
			MouseFunction.STAMP_DEPARTMENTAL,
			MouseFunction.STAMP_FOR_COMMENT,
			MouseFunction.STAMP_TOP_SECRET,
			MouseFunction.STAMP_DRAFT,
			MouseFunction.STAMP_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CUSTOM
		]

		if (!mouseFunctions.includes(this.props.mouseFunction!)) {
			return null;
		}

		let opacity: number = FunctionHelper.applyValue(this.props.opacity, 100) / 100;
		let color: string = FunctionHelper.applyValue(this.props.interiorColor, "#F0F0F0");
		let borderColor = FunctionHelper.applyValue(this.props.borderColor, "#BBBBBB");
		let fontColor = FunctionHelper.applyValue(this.props.fontColor, "#666666");
		let fontSize = FunctionHelper.applyValue(this.props.fontSize, 26);

		switch (this.props.mouseFunction) {
			case MouseFunction.STAMP_APPROVED:
				return (
					<ApprovedStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
								   language={this.props.language} onMoveStart={this.handleMove}
								   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_EXPERIMENTAL:
				return (
					<ExperimentalStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									   language={this.props.language} onMoveStart={this.handleMove}
									   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_NOT_APPROVED:
				return (
					<NotApprovedStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									  language={this.props.language} onMoveStart={this.handleMove}
									  onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_AS_IS:
				return (
					<AsIsStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
							   language={this.props.language} onMoveStart={this.handleMove}
							   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_EXPIRED:
				return (
					<ExpiredStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
								  language={this.props.language} onMoveStart={this.handleMove}
								  onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE:
				return (
					<NotForPublicReleaseStamp width={this.state.rect.width!} height={this.state.rect.height!}
											  opacity={opacity} language={this.props.language}
											  onClick={this.handleClick} onContextMenu={this.handleClick}
											  onMoveStart={this.handleMove}/>
				);
			case MouseFunction.STAMP_CONFIDENTIAL:
				return (
					<ConfidentialStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									   language={this.props.language} onMoveStart={this.handleMove}
									   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_FINAL:
				return (
					<FinalStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
								language={this.props.language} onMoveStart={this.handleMove}
								onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_SOLD:
				return (
					<SoldStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
							   language={this.props.language} onMoveStart={this.handleMove}
							   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_DEPARTMENTAL:
				return (
					<DepartmentalStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									   language={this.props.language} onMoveStart={this.handleMove}
									   onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_FOR_COMMENT:
				return (
					<ForCommentStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									 language={this.props.language} onMoveStart={this.handleMove}
									 onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_TOP_SECRET:
				return (
					<TopSecretStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
									language={this.props.language} onMoveStart={this.handleMove}
									onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_DRAFT:
				return (
					<DraftStamp width={this.state.rect.width!} height={this.state.rect.height!} opacity={opacity}
								language={this.props.language} onMoveStart={this.handleMove}
								onClick={this.handleClick} onContextMenu={this.handleClick}/>
				);
			case MouseFunction.STAMP_FOR_PUBLIC_RELEASE:
				return (
					<ForPublicReleaseStamp width={this.state.rect.width!} height={this.state.rect.height!}
										   opacity={opacity} language={this.props.language}
										   onClick={this.handleClick} onContextMenu={this.handleClick}
										   onMoveStart={this.handleMove}/>
				);
			case MouseFunction.STAMP_CUSTOM:
				return (
					<CustomStamp width={this.state.rect.width!} height={this.state.rect.height!}
								 opacity={opacity} language={this.props.language} onMoveStart={this.handleMove}
								 borderColor={borderColor} fontColor={fontColor} fontSize={fontSize}
								 onClick={this.handleClick} onContextMenu={this.handleClick}
								 color={color}>
						{this.props.content}
					</CustomStamp>
				);
		}
	}

	private renderNotePreview(): ReactNode {
		let mouseFunctions: Array<MouseFunction> = [
			MouseFunction.NOTE
		]

		if (!mouseFunctions.includes(this.props.mouseFunction!)) {
			return null;
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let width: number = UnitHelper.unitToPx(
			noteIconSize, LengthUnit.MILLIMETER, this.props.portalResolution
		) * zoomScale;
		let height: number = UnitHelper.unitToPx(
			noteIconSize, LengthUnit.MILLIMETER, this.props.portalResolution
		) * zoomScale;
		let color: string = FunctionHelper.applyValue(this.props.interiorColor, "#00FF00");
		let icon: TextAnnotationIcons = FunctionHelper.applyValue(this.props.icon, TextAnnotationIcons.Comment);
		let opacity: number = FunctionHelper.applyValue(this.props.opacity, 100) / 100;

		let iconElement: ReactNode;
		switch (icon) {
			case TextAnnotationIcons.Help:
				iconElement = <HelpIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Insert:
				iconElement =
					<InsertIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Key:
				iconElement =
					<KeyIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.NewParagraph:
				iconElement =
					<NewParagraphIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Paragraph:
				iconElement =
					<ParagraphIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Note:
				iconElement =
					<NoteIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Check:
				iconElement =
					<CheckIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Checkmark:
				iconElement =
					<CheckIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Circle:
				iconElement =
					<CircleIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Cross:
				iconElement =
					<CrossIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.CrossHair:
				iconElement =
					<CrossHairIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.RightArrow:
				iconElement =
					<RightArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.RightPointer:
				iconElement =
					<RightPointerIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.Star:
				iconElement =
					<StarIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.UpArrow:
				iconElement =
					<UpArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			case TextAnnotationIcons.UpLeftArrow:
				iconElement =
					<UpLeftArrowIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
			default:
				iconElement =
					<CommentIcon {...this.props} opacity={opacity} width={width} height={height} color={color}/>;
				break;
		}

		return (
			<>
				<div className="movearea" onPointerDown={this.handleMove} onClick={this.handleClick}
					 onContextMenu={this.handleClick} style={{
					width: "100%",
					height: "100%"
				}}/>
				{iconElement}
			</>
		)
	}

	private handleClick(e: React.MouseEvent): void {
		if (e.button === 2 && FunctionHelper.exists(this.contextMenu.current)) {
			e.preventDefault();
			e.stopPropagation();
			this.contextMenu.current!.show(e);
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let self: RectSelection = this;
		let selectionClasses: Array<string> = ["rect-selection"];

		if (this.props.mouseMode === MouseMode.RECTANGLE) {
			selectionClasses.push("moveable");
		}

		if (this.state.active) {
			selectionClasses.push("active");
		}

		let previewTypes: Array<MouseFunction> = [
			MouseFunction.SQUARE,
			MouseFunction.CIRCLE,
			MouseFunction.REDACT_AREA,
			MouseFunction.STAMP_APPROVED,
			MouseFunction.STAMP_EXPERIMENTAL,
			MouseFunction.STAMP_NOT_APPROVED,
			MouseFunction.STAMP_AS_IS,
			MouseFunction.STAMP_EXPIRED,
			MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CONFIDENTIAL,
			MouseFunction.STAMP_FINAL,
			MouseFunction.STAMP_SOLD,
			MouseFunction.STAMP_DEPARTMENTAL,
			MouseFunction.STAMP_FOR_COMMENT,
			MouseFunction.STAMP_TOP_SECRET,
			MouseFunction.STAMP_DRAFT,
			MouseFunction.STAMP_FOR_PUBLIC_RELEASE,
			MouseFunction.STAMP_CUSTOM,
			MouseFunction.NOTE
		];

		if (previewTypes.includes(this.props.mouseFunction!)) {
			selectionClasses.push("preview");
		}

		let info: string = "";
		switch (this.props.mouseFunction) {
			case MouseFunction.SIGNATURE:
				info = Translate.value("viewerscreen.area.signature", {ns: "screen"});
				break;
			case MouseFunction.WATERMARK:
				info = Translate.value("viewerscreen.area.watermark", {ns: "screen"});
				break;
			case MouseFunction.BARCODE_DETECT:
				info = Translate.value("viewerscreen.area.barcode.detect", {ns: "screen"});
				break;
			case MouseFunction.BARCODE_CREATE:
				info = Translate.value("viewerscreen.area.barcode.create", {ns: "screen"});
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_CODE:
				info = Translate.value("viewerscreen.area.barcode.swiss_code", {ns: "screen"});
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_BILL:
				info = Translate.value("viewerscreen.area.barcode.swiss_bill", {ns: "screen"});
				break;
			case MouseFunction.BARCODE_CREATE_SWISS_BILL_WITH_SPACE:
				info = Translate.value("viewerscreen.area.barcode.swiss_bill_space", {ns: "screen"});
				break;
			case MouseFunction.REDACT_AREA:
				info = Translate.value("viewerscreen.area.redact", {ns: "screen"});
				break;
			case MouseFunction.SQUARE:
				info = Translate.value("viewerscreen.area.square", {ns: "screen"});
				break;
			case MouseFunction.CIRCLE:
				info = Translate.value("viewerscreen.area.circle", {ns: "screen"});
				break;
			case MouseFunction.STAMP_APPROVED:
			case MouseFunction.STAMP_EXPERIMENTAL:
			case MouseFunction.STAMP_NOT_APPROVED:
			case MouseFunction.STAMP_AS_IS:
			case MouseFunction.STAMP_EXPIRED:
			case MouseFunction.STAMP_NOT_FOR_PUBLIC_RELEASE:
			case MouseFunction.STAMP_CONFIDENTIAL:
			case MouseFunction.STAMP_FINAL:
			case MouseFunction.STAMP_SOLD:
			case MouseFunction.STAMP_DEPARTMENTAL:
			case MouseFunction.STAMP_FOR_COMMENT:
			case MouseFunction.STAMP_TOP_SECRET:
			case MouseFunction.STAMP_DRAFT:
			case MouseFunction.STAMP_FOR_PUBLIC_RELEASE:
				info = Translate.value("viewerscreen.area.stamp", {ns: "screen"});
				break;
		}

		let contextMenu: Array<MenuItem> = [
			{
				label: Translate.value("buttons.cancel"),
				icon: 'icon icon-close',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(null, false);
					}

					self.setState({
						active: false
					});
				}
			}
		];

		if (this.props.confirmation) {
			contextMenu = [{
				label: Translate.value("buttons.confirm"),
				icon: 'icon icon-check',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(self.state.rect, true);
					}
				}
			}, ...contextMenu];
		}

		contextMenu = [{
			label: Translate.value("buttons.edit"),
			icon: 'icon icon-resize',
			command(e) {
				self.setState({
					showBoxEditDialog: true
				});
			}
		}, ...contextMenu];

		return (
			<>
				<div className={selectionClasses.join(" ")} style={{
					width: this.state.rect.width!,
					height: this.state.rect.height!,
					left: this.state.rect.x!,
					top: this.state.rect.y!
				}}>
					<div className={"popup" + (!this.state.edit ? " active" : "")}>
						<div className="popup-content">
							<MenuButton {...this.props} icon="icon icon-resize"
										tooltip={Translate.value("buttons.edit")}
										onClick={() => this.setState({
											showBoxEditDialog: true
										})}
							/>
							{this.props.confirmation &&
                                <MenuButton {...this.props} icon="icon icon-check"
                                            tooltip={Translate.value("buttons.confirm")}
                                            onClick={() => {
												if (FunctionHelper.exists(this.props.endCallback)) {
													this.props.endCallback!(this.state.rect, true);
												}
											}}
                                />
							}
							<MenuButton {...this.props} icon="icon icon-close"
										tooltip={Translate.value("buttons.cancel")}
										onClick={() => {
											if (FunctionHelper.exists(this.props.endCallback)) {
												this.props.endCallback!(null, false);
											}

											this.setState({
												active: false
											});
										}}
							/>
						</div>
					</div>
					{!this.state.edit && info !== "" &&
                        <div className="info">{info}</div>
					}
					<div className="backdrop" onPointerDown={this.handleMove} onDoubleClick={this.handleDoubleClick}
						 onClick={this.handleClick} onContextMenu={this.handleClick}/>
					{this.renderRectPreview()}
					{this.renderCirclePreview()}
					{this.renderStampPreview()}
					{this.renderNotePreview()}
					{this.props.resizeable &&
                        <>
                            <div className="resize top left"/>
                            <div className="resize-area top left"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.TOP_LEFT, event)}/>
                            <div className="resize top center horizontal"/>
                            <div className="resize-area top center horizontal"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.TOP_CENTER, event)}/>
                            <div className="resize top right"/>
                            <div className="resize-area top right"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.TOP_RIGHT, event)}/>
                            <div className="resize left center vertical"/>
                            <div className="resize-area left center vertical"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.CENTER_LEFT, event)}/>
                            <div className="resize right center vertical"/>
                            <div className="resize-area right center vertical"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.CENTER_RIGHT, event)}/>
                            <div className="resize bottom left"/>
                            <div className="resize-area bottom left"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.BOTTOM_LEFT, event)}/>
                            <div className="resize bottom center horizontal"/>
                            <div className="resize-area bottom center horizontal"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.BOTTOM_CENTER, event)}/>
                            <div className="resize bottom right"/>
                            <div className="resize-area bottom right"
                                 onPointerDown={(event: React.MouseEvent) =>
									 this.handleResize(ResizeDirection.BOTTOM_RIGHT, event)}/>
                        </>
					}
				</div>
				<ContextMenu appendTo={document.getElementById("root")} model={contextMenu}
							 ref={this.contextMenu}/>
				<AnnotationEditBoxDialog {...this.props} showHelp={false} parameter={this.state.rect}
										 getParent={this.props.getParent} zoomValue={this.props.zoomValue}
										 visible={this.state.showBoxEditDialog}
										 fixedWidth={FunctionHelper.exists(this.props.width)}
										 fixedHeight={FunctionHelper.exists(this.props.height)}
										 hide={() => this.setState({
											 showBoxEditDialog: false
										 })}
										 submit={(result: Point | Rectangle | Array<Point>) => {
											 if (result instanceof Rectangle) {
												 this.setState({
													 rect: result.clone()
												 }, async () => {
													 if (FunctionHelper.exists(this.props.endCallback)) {
														 await this.props.endCallback!(this.state.rect, true);
													 }
												 });
											 }
										 }}/>
			</>
		);
	}
}